import { useState } from 'react';
import { Avatar, ModalFooter, Seg, saveBlockers, useToast, writeWithToast, useDirtyDraft } from '../common';
import { api } from '../../lib/api';
import { ROLE_BLURB, ROLES, type Role, type User } from './types';
import { errMsg, fmtAdded } from './utils';

// Add-or-edit access drawer — the one drawer for both, opened with `user: null`
// to add (matching OwnerDrawer). On ADD the email is an editable field; on EDIT
// it is the Google identity and shows read-only, role being the only editable
// field. Both save through the upsert POST ({ email, role }); on edit the server
// preserves the original adder (AC5). Edit also offers Remove in the footer
// via the shared ModalFooter inline confirm (AC6). Self-edit is guarded: an admin can't demote OR
// remove themselves (server blocks both; the UI disables them up front).
interface UserPaneProps {
  // null → add a new user; a User → edit it.
  user: User | null;
  myEmail: string;
  onClose: () => void;
  onSaved: () => void;
  onRemoved: () => void;
}

export function UserPane({ user, myEmail, onClose, onSaved, onRemoved }: UserPaneProps) {
  const isCreate = user === null;
  const isMe = !isCreate && user.email === myEmail;
  // A CUSTOMER IS NOT EDITED HERE. Their entry carries the household and
  // contact they are bound to; saving it through this pane would rewrite it as
  // a bare desk role and drop that binding on the floor — the person would keep
  // signing in and land on nothing. Their access is issued, reset and revoked
  // from the household's App access column, and this pane says so.
  const isCustomer = !isCreate && user.role === 'customer';
  const [email, setEmail] = useState(user?.email ?? '');
  const [role, setRole] = useState<Role>(user && user.role !== 'customer' ? user.role : 'helper');
  const [error, setError] = useState<string | null>(null);
  // The unsaved-changes guard's one line.
  useDirtyDraft('user', 'this person', { email, role });
  const toast = useToast();

  const save = () => {
    const e = isCreate ? email.trim().toLowerCase() : user.email;
    if (isCreate && !e.includes('@')) {
      setError('Enter a valid email.');
      return;
    }
    // An edit that changes nothing is just a close.
    if (!isCreate && role === user.role) {
      onClose();
      return;
    }
    const roleToSave = role;
    // Close instantly; the upsert runs in the background. writeWithToast owns the
    // close-first + toast + 401-guard shape (a 401 has already redirected to
    // login, so it must NOT error-toast over the bounce — the guard this pane was
    // previously missing).
    void writeWithToast(toast, {
      closeFirst: onClose,
      write: () => api('/account/users', { method: 'POST', body: { email: e, role: roleToSave } }),
      success: isCreate ? 'Access granted' : 'Role updated',
      error: (err) => errMsg(err, isCreate ? 'Could not add this person.' : 'Could not save the change.'),
      onSaved,
    });
  };

  // Two-step confirm lives in the shared footer (ModalFooter danger.confirm); this
  // just performs the delete once confirmed. Optimistic: close, then DELETE.
  const remove = () => {
    if (isCreate) return;
    const email = user.email;
    void writeWithToast(toast, {
      closeFirst: onClose,
      write: () => api(`/account/users/${encodeURIComponent(email)}`, { method: 'DELETE' }),
      success: 'Access removed',
      error: (err) => errMsg(err, 'Could not remove access.'),
      onSaved: onRemoved,
    });
  };

  return (
    <div className="pane anim-fade">
      <div className="pane-scroll">
      {isCreate ? (
        <div className="field">
          <label htmlFor="account-add-email">Email</label>
          <input
            id="account-add-email"
            type="email"
            placeholder="name@example.com"
            value={email}
            autoFocus
            onChange={(ev) => setEmail(ev.target.value)}
            onKeyDown={(ev) => {
              if (ev.key === 'Enter') save();
            }}
          />
          <p className="account-drawer-hint">They sign in with this Google account.</p>
          {error && <p className="account-drawer-hint" style={{ color: 'var(--alert-deep)' }}>{error}</p>}
        </div>
      ) : (
        <div className="account-drawer-id">
          <Avatar name={user.email} id={user.email} size={40} />
          <div className="account-drawer-id-text">
            <span className="account-drawer-email">{user.email}</span>
            {isMe && <span className="account-you">You</span>}
          </div>
        </div>
      )}

      <div className="field">
        <label>Role</label>
        {isMe ? (
          <p className="account-drawer-self-note">
            <span className={'account-role-pill role-' + user.role}>{user.role}</span>
            You can’t change your own admin access.
          </p>
        ) : isCustomer ? (
          <p className="account-drawer-self-note">
            <span className="account-role-pill role-customer">customer</span>
            Managed from their household — open it and use App access.
          </p>
        ) : (
          <>
            <Seg
              value={role}
              onChange={(v) => setRole(v as Role)}
              options={ROLES.map((r) => ({ value: r, label: r[0]!.toUpperCase() + r.slice(1) }))}
            />
            <p className="account-drawer-hint">{ROLE_BLURB[role]}.</p>
          </>
        )}
      </div>

      {!isCreate && (
        <div className="account-drawer-meta">
          <div>
            <span className="account-drawer-meta-label">Created by</span>
            <span className="account-drawer-meta-val">{user.addedBy ?? 'Seeded'}</span>
          </div>
          <div>
            <span className="account-drawer-meta-label">Added</span>
            <span className="account-drawer-meta-val">{fmtAdded(user.addedAt)}</span>
          </div>
        </div>
      )}
        </div>
        <ModalFooter
          onCancel={onClose}
          onSave={save}
          saveLabel={isCreate ? 'Add' : 'Save'}
          // Why Add cannot be pressed, in the words to show — see ModalFooter's
          // `blockers`. A greyed button that explains nothing is the defect this
          // replaces, and there is no boolean form left to fall back on.
          blockers={saveBlockers(
            isCreate && !email.trim() && 'Enter the email address to invite.',
            isCustomer && 'A customer’s access is changed from their household, not here.',
          )}
          // The same inline two-step confirm as every other modal delete — no
          // separate dialog. Self-removal is blocked (the server guards it too).
          // A CUSTOMER has no remove here either: this delete clears the
          // allowlist entry only, and the household's Revoke also tombstones
          // the invite — without that they derive to *Signing up* on the desk
          // for good, with no reason recorded (PR #223 review).
          danger={
            !isCreate && !isMe && !isCustomer
              ? {
                  label: 'Remove access',
                  confirm: {
                    message: `${user.email} will no longer be able to sign in.`,
                    confirmLabel: 'Remove access',
                    onConfirm: remove,
                  },
                }
              : null
          }
        />
    </div>
  );
}
